import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import SplitEditor from "../components/SplitEditor";
import {
  ArrowLeft,
  Download,
  ExternalLink,
  Loader2,
  Music2,
  Disc,
  FileText,
  Users,
  Link as LinkIcon,
  CheckCircle2,
  AlertCircle,
  Clock,
  RefreshCw,
  Copy,
  Shield,
  Database,
  Plus,
  UserPlus,
  Building2,
  Sparkles,
  Zap,
  MapPin,
  Mail,
  Globe,
  Filter,
  ChevronDown,
  ChevronUp,
  Target,
  HelpCircle,
  Pencil,
  Save,
  X,
  Trash2,
  ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  completed: { icon: CheckCircle2, color: "text-syncchain-success", bg: "bg-syncchain-success/10" },
  failed: { icon: AlertCircle, color: "text-syncchain-error", bg: "bg-syncchain-error/10" },
  searching: { icon: Loader2, color: "text-syncchain-warning", bg: "bg-syncchain-warning/10", animate: true },
  queued: { icon: Clock, color: "text-syncchain-info", bg: "bg-syncchain-info/10" },
  partial: { icon: AlertCircle, color: "text-purple-400", bg: "bg-purple-500/10" },
};

// Helper component for metadata rows
const MetadataRow = ({ label, value, mono, copyable, onCopy, link }) => (
  <div className="space-y-1">
    <p className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted">
      {label}
    </p>
    <div className="flex items-center gap-2">
      {link && value ? (
        <a 
          href={link} 
          target="_blank" 
          rel="noopener noreferrer"
          className={`${mono ? "font-mono" : ""} text-syncchain-text hover:text-syncchain-accent transition-colors flex items-center gap-1`}
        >
          {value || "—"}
          <ExternalLink className="w-3 h-3" />
        </a>
      ) : (
        <p className={`${mono ? "font-mono" : ""} text-syncchain-text`}>
          {value || "—"}
        </p>
      )}
      {copyable && value && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-syncchain-text-muted hover:text-syncchain-accent"
          onClick={() => onCopy(value, label)}
        >
          <Copy className="w-3 h-3" />
        </Button>
      )}
    </div>
  </div>
);

// Quick Add Panel for manual data entry
const QuickAddPanel = ({ onAddWriter, onAddPublisher }) => {
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState("writer");
  const [name, setName] = useState("");
  const [ipi, setIpi] = useState("");

  const handleAdd = () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }

    const entry = {
      name: name.trim(),
      ipi: ipi.trim() || null,
      role: formType,
      authority: "manual"
    };

    if (formType === "writer") {
      onAddWriter(entry);
      toast.success(`Writer "${name}" added`);
    } else {
      onAddPublisher(entry);
      toast.success(`Publisher "${name}" added`);
    }

    // Reset form
    setName("");
    setIpi("");
  };

  return (
    <Card className="bg-syncchain-surface/50 border-white/10 mb-4">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-syncchain-accent/10 flex items-center justify-center">
              <Database className="w-5 h-5 text-syncchain-accent" />
            </div>
            <div>
              <p className="font-medium text-syncchain-text">Quick Add Entry</p>
              <p className="text-xs text-syncchain-text-muted">
                Manually add writers or publishers
              </p>
            </div>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            variant={showForm ? "secondary" : "default"}
            className={showForm ? "bg-white/10" : "bg-syncchain-accent hover:bg-syncchain-accent-hover text-white"}
            size="sm"
            data-testid="quick-add-toggle"
          >
            <Plus className={`w-4 h-4 mr-1 transition-transform ${showForm ? "rotate-45" : ""}`} />
            {showForm ? "Close" : "Add Entry"}
          </Button>
        </div>

        {showForm && (
          <div className="mt-4 p-4 bg-syncchain-bg rounded-lg border border-white/10 animate-slide-up">
            <div className="flex gap-2 mb-4">
              <Button
                variant={formType === "writer" ? "default" : "outline"}
                size="sm"
                onClick={() => setFormType("writer")}
                className={formType === "writer" ? "bg-syncchain-accent" : "border-white/10"}
              >
                <UserPlus className="w-4 h-4 mr-1" />
                Writer
              </Button>
              <Button
                variant={formType === "publisher" ? "default" : "outline"}
                size="sm"
                onClick={() => setFormType("publisher")}
                className={formType === "publisher" ? "bg-syncchain-accent" : "border-white/10"}
              >
                <Building2 className="w-4 h-4 mr-1" />
                Publisher
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted mb-1 block">
                  {formType === "writer" ? "Writer Name" : "Publisher Name"} *
                </Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={formType === "writer" ? "e.g., John Smith" : "e.g., Universal Music"}
                  className="bg-syncchain-surface border-white/10 text-syncchain-text h-9"
                  data-testid="quick-add-name"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted mb-1 block">
                  IPI #
                </Label>
                <Input
                  value={ipi}
                  onChange={(e) => setIpi(e.target.value)}
                  placeholder="e.g., 123456789"
                  className="bg-syncchain-surface border-white/10 text-syncchain-text h-9 font-mono"
                  data-testid="quick-add-ipi"
                />
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <Button
                onClick={handleAdd}
                className="bg-syncchain-success hover:bg-syncchain-success/90 text-white"
                size="sm"
                data-testid="quick-add-submit"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add {formType === "writer" ? "Writer" : "Publisher"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SearchDetail = () => {
  const { id } = useParams();
  const [search, setSearch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [proData, setProData] = useState({ writers: [], publishers: [], contactInfo: [], allIswcs: [], unmatchedWriters: [], filterStats: null });
  const [fetchingPro, setFetchingPro] = useState(false);
  const [creditsData, setCreditsData] = useState({ writers: [], producers: [], engineers: [], musicians: [] });
  const [fetchingCredits, setFetchingCredits] = useState(false);
  const [showUnmatched, setShowUnmatched] = useState(true);  // Default to expanded since these are legitimate writers
  const [selectedWriters, setSelectedWriters] = useState(new Set());
  const [selectedPublishers, setSelectedPublishers] = useState(new Set());
  const [prsStatus, setPrsStatus] = useState({ configured: false, username: null });
  const [fetchingPrs, setFetchingPrs] = useState(false);
  const [prsData, setPrsData] = useState(null);
  const [showPrsConfig, setShowPrsConfig] = useState(false);
  const [prsCredentials, setPrsCredentials] = useState({ username: '', password: '' });
  
  // Recording metadata edit state
  const [editingRecording, setEditingRecording] = useState(false);
  const [editedRecording, setEditedRecording] = useState({});
  const [savingRecording, setSavingRecording] = useState(false);

  // Credits edit state
  const [editingCredits, setEditingCredits] = useState(false);
  const [editedCredits, setEditedCredits] = useState({ writers: [], producers: [], engineers: [], musicians: [] });
  const [savingCredits, setSavingCredits] = useState(false);
  const [newCreditEntry, setNewCreditEntry] = useState({ name: '', role: '', category: 'writers' });

  const fetchSearch = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/search/${id}`);
      setSearch(response.data);
      setNotFound(false);
      setRetryCount(0);
      
      // Load saved credits from results_json if available
      const savedCredits = response.data?.results_json?.credits;
      if (savedCredits && (savedCredits.writers?.length > 0 || savedCredits.producers?.length > 0)) {
        setCreditsData(savedCredits);
      }

      // Auto-load publishing/PRO data from pipeline results
      const pub = response.data?.results_json?.publishing;
      if (pub && (pub.writers?.length > 0 || pub.publishers?.length > 0)) {
        setProData(prev => ({
          writers: pub.writers || prev?.writers || [],
          publishers: pub.publishers || prev?.publishers || [],
          mainPublishers: pub.main_publishers || prev?.mainPublishers || [],
          subPublishers: pub.sub_publishers || prev?.subPublishers || [],
          contactInfo: pub.publisher_contacts || prev?.contactInfo || [],
          allIswcs: pub.iswc ? [{ code: pub.iswc }] : (prev?.allIswcs || []),
          unmatchedWriters: prev?.unmatchedWriters || [],
          filterStats: prev?.filterStats,
          controlledPercentages: pub.controlled_percentages || prev?.controlledPercentages
        }));
      }
      
      // Continue polling if still searching or pipeline is in progress
      const pipelineActive = response.data.pipeline_status && 
        !['completed', 'manual_split_verification_required', 'failed'].includes(response.data.pipeline_status);
      if (response.data.status === "searching" || response.data.status === "queued" || pipelineActive) {
        setPolling(true);
      } else {
        setPolling(false);
      }
    } catch (error) {
      console.error("Failed to fetch search:", error);
      if (error.response?.status === 404 && retryCount < 5) {
        setRetryCount(prev => prev + 1);
        setPolling(true);
        return;
      }
      setNotFound(true);
      setPolling(false);
    } finally {
      setLoading(false);
    }
  }, [id, retryCount]);

  // Check PRS configuration status on mount
  useEffect(() => {
    const checkPrsStatus = async () => {
      try {
        const response = await axios.get(`${API}/prs/status`);
        setPrsStatus(response.data);
      } catch (error) {
        console.log('PRS status check failed:', error);
      }
    };
    checkPrsStatus();
  }, []);

  // Auto-poll for PRS completion when status is queued/processing
  useEffect(() => {
    const prsState = search?.results_json?.publishing?.prs_status;
    if (prsState !== 'prs_queued' && prsState !== 'prs_processing') return;

    const pollPrs = setInterval(async () => {
      try {
        const resp = await axios.get(`${API}/search/${id}`);
        const newStatus = resp.data?.results_json?.publishing?.prs_status;
        if (newStatus && newStatus !== 'prs_queued' && newStatus !== 'prs_processing') {
          setSearch(resp.data);
          // Update proData with fresh publishing data
          const pub = resp.data?.results_json?.publishing;
          if (pub && (pub.writers?.length > 0 || pub.publishers?.length > 0)) {
            setProData(prev => ({
              writers: pub.writers || prev?.writers || [],
              publishers: pub.publishers || prev?.publishers || [],
              mainPublishers: pub.main_publishers || prev?.mainPublishers || [],
              subPublishers: pub.sub_publishers || prev?.subPublishers || [],
              contactInfo: pub.publisher_contacts || prev?.contactInfo || [],
              allIswcs: pub.iswc ? [{ code: pub.iswc }] : (prev?.allIswcs || []),
              unmatchedWriters: prev?.unmatchedWriters || [],
              filterStats: prev?.filterStats,
              controlledPercentages: pub.controlled_percentages || prev?.controlledPercentages
            }));
          }
          setFetchingPrs(false);
          clearInterval(pollPrs);
        }
      } catch (e) {
        console.error("PRS polling error:", e.message);
      }
    }, 4000);

    return () => clearInterval(pollPrs);
  }, [search?.results_json?.publishing?.prs_status, id]);

  useEffect(() => {
    fetchSearch();
  }, [fetchSearch]);

  useEffect(() => {
    if (polling) {
      const interval = setInterval(fetchSearch, 1500);
      return () => clearInterval(interval);
    }
  }, [polling, fetchSearch]);

  // Configure PRS credentials
  const handleConfigurePrs = async () => {
    if (!prsCredentials.username || !prsCredentials.password) {
      toast.error('Please enter both username and password');
      return;
    }
    try {
      const response = await axios.post(`${API}/prs/configure`, prsCredentials);
      toast.success('PRO credentials saved successfully');
      setPrsStatus({ configured: true, username: response.data.username });
      setShowPrsConfig(false);
      setPrsCredentials({ username: '', password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save PRO credentials');
    }
  };

  // Fetch PRS data with member login (now queue-based)
  const fetchPrsData = async () => {
    if (!prsStatus.configured) {
      setShowPrsConfig(true);
      return;
    }
    
    setFetchingPrs(true);
    toast.info("PRO share enrichment queued... Processing in background");
    
    try {
      const response = await axios.post(`${API}/search/${id}/fetch-prs-data`, {}, { timeout: 15000 });
      const jobStatus = response.data.status;
      
      if (jobStatus === 'prs_queued' || jobStatus === 'prs_processing') {
        // Poll for completion
        let attempts = 0;
        const maxAttempts = 30; // 30 * 3s = 90s max
        const poll = async () => {
          try {
            const statusResp = await axios.get(`${API}/prs/queue/${id}`);
            const currentStatus = statusResp.data.status;
            
            if (currentStatus === 'prs_completed') {
              // Reload search data to get merged PRS results
              const searchResp = await axios.get(`${API}/search/${id}`);
              setSearch(searchResp.data);
              const updatedPub = searchResp.data?.results_json?.publishing;
              if (updatedPub) {
                setProData(prev => ({
                  writers: updatedPub.writers || prev?.writers || [],
                  publishers: updatedPub.publishers || prev?.publishers || [],
                  mainPublishers: updatedPub.main_publishers || prev?.mainPublishers || [],
                  subPublishers: updatedPub.sub_publishers || prev?.subPublishers || [],
                  contactInfo: updatedPub.publisher_contacts || prev?.contactInfo || [],
                  allIswcs: updatedPub.iswc ? [{ code: updatedPub.iswc }] : (prev?.allIswcs || []),
                  unmatchedWriters: prev?.unmatchedWriters || [],
                  filterStats: prev?.filterStats,
                  controlledPercentages: updatedPub.controlled_percentages || prev?.controlledPercentages
                }));
                toast.success(`PRO enrichment complete! Updated ${updatedPub.writers?.length || 0} writers and ${updatedPub.publishers?.length || 0} publishers`);
              }
              setFetchingPrs(false);
              return;
            } else if (currentStatus === 'prs_failed' || currentStatus === 'prs_skipped') {
              const error = statusResp.data.job?.error || 'PRO enrichment failed';
              toast.error(`PRO: ${error}`);
              setFetchingPrs(false);
              return;
            } else if (currentStatus === 'no_results') {
              toast.warning("PRO: No confident match found for this work on PRS");
              // Reload search data to get updated status
              const searchResp = await axios.get(`${API}/search/${id}`);
              setSearch(searchResp.data);
              setFetchingPrs(false);
              return;
            }
            
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(poll, 3000);
            } else {
              toast.warning("PRO enrichment is still processing. Check back later.");
              setFetchingPrs(false);
            }
          } catch (e) {
            setFetchingPrs(false);
          }
        };
        setTimeout(poll, 5000); // Start polling after 5s
      } else {
        toast.info(`PRO status: ${jobStatus}`);
        setFetchingPrs(false);
      }
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('credentials')) {
        setShowPrsConfig(true);
        toast.error('PRO credentials required. Please configure your login.');
      } else if (error.response?.status === 400 && error.response?.data?.detail?.includes('disabled')) {
        toast.error('PRO enrichment is currently disabled');
      } else {
        toast.error(error.response?.data?.detail || "Failed to queue PRO enrichment");
      }
      setFetchingPrs(false);
    }
  };

  const handleExportJSON = async () => {
    try {
      const response = await axios.get(`${API}/export/search/${id}.json`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `search_${id}.json`);
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('JSON exported successfully');
    } catch (error) {
      console.error('Export JSON error:', error);
      toast.error('Failed to export JSON');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/export/search/${id}.csv`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `search_${id}.csv`);
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Export CSV error:', error);
      toast.error('Failed to export CSV');
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      // Try the modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard`);
        return;
      }
    } catch (err) {
      // Clipboard API failed, try fallback
      console.warn("Clipboard API failed, using fallback:", err);
    }
    
    // Fallback: Use a temporary textarea element
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      
      if (successful) {
        toast.success(`${label} copied to clipboard`);
      } else {
        toast.error(`Failed to copy ${label}`);
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      toast.error(`Failed to copy ${label}`);
    }
  };

  const handleSplitsUpdate = async (splits) => {
    try {
      await axios.patch(`${API}/search/${id}`, {
        overrides: {},
        splits: splits,
        edited_by: "admin",
      });
      toast.success("Splits saved successfully");
      fetchSearch();
    } catch (error) {
      toast.error("Failed to save splits");
    }
  };

  const fetchProData = async (forceRefresh = false) => {
    setFetchingPro(true);
    const refreshText = forceRefresh ? " (forcing refresh)" : "";
    toast.info(`Smart fetching PRO data with credit verification${refreshText}... This may take 15-30 seconds`);
    try {
      // Use the smart fetch endpoint that filters PRO results using verified credits
      const url = forceRefresh 
        ? `${API}/search/${id}/smart-fetch-pro-data?skip_cache=true`
        : `${API}/search/${id}/smart-fetch-pro-data`;
      const response = await axios.post(url, {}, { timeout: 180000 });
      const data = response.data.pro_data;
      const credits = response.data.credits_data;
      const filterStats = data.filter_stats;
      const fromCache = data.from_cache;
      
      // Also update credits data if we got new data
      if (credits && (credits.writers?.length > 0 || credits.producers?.length > 0)) {
        setCreditsData(prev => {
          const dedupeByName = (existing, newItems) => {
            const seen = new Set((existing || []).map(item => item.name?.toUpperCase()));
            const unique = [...(existing || [])];
            for (const item of (newItems || [])) {
              const key = item.name?.toUpperCase();
              if (key && !seen.has(key)) {
                seen.add(key);
                unique.push(item);
              }
            }
            return unique;
          };
          
          return {
            writers: dedupeByName(prev?.writers, credits.writers),
            producers: dedupeByName(prev?.producers, credits.producers),
            engineers: dedupeByName(prev?.engineers, credits.engineers),
            musicians: dedupeByName(prev?.musicians, credits.musicians),
            iswc: credits.iswc || prev?.iswc
          };
        });
      }
      
      if (data.writers?.length > 0 || data.publishers?.length > 0 || data.allIswcs?.length > 0 || data.unmatched_writers?.length > 0 || data.main_publishers?.length > 0) {
        setProData(prev => {
          // Helper to dedupe by name (case-insensitive)
          const dedupeByName = (existing, newItems) => {
            const seen = new Set((existing || []).map(item => item.name?.toUpperCase()));
            const unique = [...(existing || [])];
            for (const item of (newItems || [])) {
              const key = item.name?.toUpperCase();
              if (key && !seen.has(key)) {
                seen.add(key);
                unique.push(item);
              }
            }
            return unique;
          };
          
          // Helper to dedupe ISWCs by code
          const dedupeIswcs = (existing, newItems) => {
            const seen = new Set((existing || []).map(item => item.code));
            const unique = [...(existing || [])];
            for (const item of (newItems || [])) {
              if (item.code && !seen.has(item.code)) {
                seen.add(item.code);
                unique.push(item);
              }
            }
            return unique;
          };
          
          return {
            writers: dedupeByName(prev?.writers, data.writers),
            publishers: dedupeByName(prev?.publishers, data.publishers),
            mainPublishers: dedupeByName([], data.main_publishers || []),
            subPublishers: dedupeByName([], data.sub_publishers || []),
            contactInfo: dedupeByName(prev?.contactInfo, data.contactInfo),
            allIswcs: dedupeIswcs(prev?.allIswcs, data.allIswcs),
            unmatchedWriters: dedupeByName([], data.unmatched_writers || []),
            filterStats: filterStats,
            bmiWorkId: data.bmi_work_id,
            controlledPercentages: data.controlled_percentages
          };
        });
        
        // Build informative message for results
        let message = "";
        if (filterStats?.filter_mode === 'pro_direct') {
          const writerCount = data.writers?.length || 0;
          const pubCount = data.publishers?.length || 0;
          const mainPubCount = data.main_publishers?.length || 0;
          const subPubCount = data.sub_publishers?.length || 0;
          message = `Found ${writerCount} writers, ${mainPubCount} main publishers`;
          if (subPubCount > 0) {
            message += `, ${subPubCount} sub-publishers`;
          }
        } else if (filterStats) {
          if (filterStats.verified_names_count > 0) {
            message = `✓ Verified ${filterStats.matched_writers_count} of ${filterStats.total_pro_writers} PRO writers against ${filterStats.verified_names_count} credits`;
            if (filterStats.unmatched_writers_count > 0) {
              message += ` (${filterStats.unmatched_writers_count} unmatched)`;
            }
          } else {
            message = `Found ${data.writers?.length || 0} writers (no credits to verify against)`;
          }
        } else {
          message = `Found ${data.writers?.length || 0} writers and ${data.publishers?.length || 0} publishers`;
        }
        
        if (data.allIswcs?.length > 0) {
          message += ` with ${data.allIswcs.length} ISWC(s)`;
        } else if (data.iswc) {
          message += ` with ISWC: ${data.iswc}`;
        }
        
        // Add cache indicator
        if (fromCache) {
          message += " ⚡ (from cache)";
        }
        
        toast.success(message);
      } else {
        toast.warning("No PRO data found. Try the manual Quick Add below.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch PRO data. Try manual entry.");
    } finally {
      setFetchingPro(false);
    }
  };

  // Save recording metadata
  const saveRecordingMetadata = async () => {
    setSavingRecording(true);
    try {
      const response = await axios.patch(`${API}/search/${id}/recording`, editedRecording);
      setSearch(response.data);
      setEditingRecording(false);
      toast.success("Recording metadata saved successfully");
    } catch (error) {
      console.error("Error saving recording metadata:", error);
      toast.error("Failed to save recording metadata");
    } finally {
      setSavingRecording(false);
    }
  };

  // Start editing recording with current values
  const startEditingRecording = () => {
    const currentRecording = results?.recording || {};
    setEditedRecording({
      title: currentRecording.title || '',
      artist: currentRecording.artist || '',
      isrc: currentRecording.isrc || '',
      upc: currentRecording.upc || '',
      label: currentRecording.label || '',
      release: currentRecording.release || '',
      releaseDate: currentRecording.releaseDate || '',
      phonographicCopyright: currentRecording.phonographicCopyright || ''
    });
    setEditingRecording(true);
  };

  // Credits editing functions
  const startEditingCredits = () => {
    setEditedCredits({
      writers: [...(creditsData?.writers || [])],
      producers: [...(creditsData?.producers || [])],
      engineers: [...(creditsData?.engineers || [])],
      musicians: [...(creditsData?.musicians || [])]
    });
    setNewCreditEntry({ name: '', role: '', category: 'writers' });
    setEditingCredits(true);
  };

  const saveCredits = async () => {
    setSavingCredits(true);
    try {
      const response = await axios.patch(`${API}/search/${id}/credits`, editedCredits);
      setSearch(response.data);
      setCreditsData(editedCredits);
      setEditingCredits(false);
      toast.success("Credits saved successfully");
    } catch (error) {
      console.error("Error saving credits:", error);
      toast.error("Failed to save credits");
    } finally {
      setSavingCredits(false);
    }
  };

  const addCreditEntry = () => {
    if (!newCreditEntry.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const role = newCreditEntry.role.trim() || getCategoryDefaultRole(newCreditEntry.category);
    const newEntry = { name: newCreditEntry.name.trim(), role };
    
    setEditedCredits(prev => ({
      ...prev,
      [newCreditEntry.category]: [...(prev[newCreditEntry.category] || []), newEntry]
    }));
    setNewCreditEntry({ name: '', role: '', category: newCreditEntry.category });
    toast.success(`Added ${newCreditEntry.name} to ${newCreditEntry.category}`);
  };

  const getCategoryDefaultRole = (category) => {
    const defaults = {
      writers: 'Writer',
      producers: 'Producer',
      engineers: 'Engineer',
      musicians: 'Musician'
    };
    return defaults[category] || 'Unknown';
  };

  const removeCreditEntry = (category, index) => {
    setEditedCredits(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
  };

  const updateCreditEntry = (category, index, field, value) => {
    setEditedCredits(prev => ({
      ...prev,
      [category]: prev[category].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const fetchCredits = async () => {
    setFetchingCredits(true);
    toast.info("Fetching verified credits...");
    try {
      const response = await axios.post(`${API}/search/${id}/fetch-credits`, {}, { timeout: 60000 });
      const data = response.data.credits;
      
      if (data.writers?.length > 0 || data.producers?.length > 0) {
        // Dedupe before setting
        const dedupeByName = (existing, newItems) => {
          const seen = new Set((existing || []).map(item => item.name?.toUpperCase()));
          const unique = [...(existing || [])];
          for (const item of (newItems || [])) {
            const key = item.name?.toUpperCase();
            if (key && !seen.has(key)) {
              seen.add(key);
              unique.push(item);
            }
          }
          return unique;
        };
        
        setCreditsData(prev => ({
          writers: dedupeByName(prev?.writers, data.writers),
          producers: dedupeByName(prev?.producers, data.producers),
          engineers: dedupeByName(prev?.engineers, data.engineers),
          musicians: dedupeByName(prev?.musicians, data.musicians),
          iswc: data.iswc || prev?.iswc
        }));
        
        toast.success(`Found ${data.writers?.length || 0} writers, ${data.producers?.length || 0} producers, ${data.engineers?.length || 0} engineers!`);
      } else {
        toast.warning("No credits found in verified databases.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch credits.");
    } finally {
      setFetchingCredits(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-syncchain-accent" />
      </div>
    );
  }

  if (!search && !polling) {
    return (
      <div className="text-center py-12">
        <p className="text-syncchain-text-secondary">{notFound ? "Search not found" : "Loading search..."}</p>
        {notFound && (
          <Button asChild variant="link" className="text-syncchain-accent mt-2">
            <Link to="/">Return to Dashboard</Link>
          </Button>
        )}
      </div>
    );
  }

  // Still waiting for initial data while polling
  if (!search && polling) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-syncchain-accent mb-4" />
        <p className="text-syncchain-text-secondary">Loading search results...</p>
        <p className="text-xs text-syncchain-text-muted mt-2">This may take a few seconds</p>
      </div>
    );
  }

  const results = search.results_json || {};
  const recording = results.recording || {};
  const publishing = results.publishing || {};
  const identifiers = results.identifiers || {};
  const evidence = results.evidence || [];
  const confidence = search.confidence || {};
  const StatusIcon = statusConfig[search.status]?.icon || Clock;

  // Derive writers and publishers from splits if available
  const splits = publishing.splits || [];
  const derivedWriters = splits.filter(s => s.role === 'writer').map(s => ({
    name: s.party,
    ipi: s.ipi,
    pro: s.pro,
    sharePct: s.sharePct,
    authority: s.authority || 'manual',
    performing_share: s.performing_share,
    mechanical_share: s.mechanical_share,
    performing_society: s.performing_society || s.prs_society,
    mechanical_society: s.mechanical_society
  }));
  const derivedPublishers = splits.filter(s => s.role === 'publisher' || s.role === 'admin').map(s => ({
    name: s.party,
    ipi: s.ipi,
    pro: s.pro,
    role: s.role,
    sharePct: s.sharePct,
    authority: s.authority || 'manual',
    performing_share: s.performing_share,
    mechanical_share: s.mechanical_share,
    performing_society: s.performing_society || s.prs_society,
    mechanical_society: s.mechanical_society
  }));
  
  // Use derived data if splits exist, otherwise fall back to original publishing data
  const displayWriters = derivedWriters.length > 0 ? derivedWriters : (publishing.writers || []);
  const displayPublishers = derivedPublishers.length > 0 ? derivedPublishers : (publishing.publishers || []);

  const formatDuration = (ms) => {
    if (!ms) return "--:--";
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="space-y-6" data-testid="search-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="text-syncchain-text-secondary hover:text-syncchain-text shrink-0"
          >
            <Link to="/">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-heading text-2xl font-bold text-syncchain-text">
                {search.track_title || search.query_title || "Unknown Track"}
              </h1>
              <Badge
                className={`${statusConfig[search.status]?.bg} ${statusConfig[search.status]?.color} border-0 text-xs font-mono uppercase`}
              >
                <StatusIcon className={`w-3 h-3 mr-1 ${statusConfig[search.status]?.animate ? "animate-spin" : ""}`} />
                {search.status}
              </Badge>
            </div>
            <p className="text-syncchain-text-secondary">
              {search.track_artist || search.query_artist || "Unknown Artist"}
            </p>
            {search.timing_ms > 0 && (
              <p className="text-xs font-mono text-syncchain-text-muted mt-1">
                Resolved in {search.timing_ms}ms
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleExportJSON}
            variant="outline"
            size="sm"
            className="border-white/10 text-syncchain-text-secondary hover:text-syncchain-text hover:bg-white/5"
            data-testid="export-json-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            JSON
          </Button>
          <Button
            onClick={handleExportCSV}
            variant="outline"
            size="sm"
            className="border-white/10 text-syncchain-text-secondary hover:text-syncchain-text hover:bg-white/5"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            CSV
          </Button>
          {polling && (
            <Button
              onClick={fetchSearch}
              variant="outline"
              size="sm"
              className="border-white/10 text-syncchain-accent"
            >
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Refreshing...
            </Button>
          )}
        </div>
      </div>

      {/* Confidence Card */}
      {confidence.overall !== undefined && (
        <Card className="bg-syncchain-paper border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-syncchain-accent" />
                <span className="text-sm font-medium text-syncchain-text">Confidence Score</span>
              </div>
              <span className="text-2xl font-heading font-bold text-syncchain-text">
                {Math.round(confidence.overall * 100)}%
              </span>
            </div>
            <Progress value={confidence.overall * 100} className="h-2 bg-syncchain-surface" />
            <div className="flex justify-between mt-3 text-xs text-syncchain-text-muted">
              <span>Recording: {Math.round((confidence.recording || 0) * 100)}%</span>
              <span>Publishing: {Math.round((confidence.publishing || 0) * 100)}%</span>
            </div>
            {confidence.rationale && (
              <p className="text-xs text-syncchain-text-secondary mt-3 p-3 bg-syncchain-surface/50 rounded-lg">
                {confidence.rationale}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pipeline Progress */}
      {search.pipeline_status && search.status !== 'completed' && search.status !== 'failed' && (
        <Card className="bg-syncchain-paper border-white/10" data-testid="pipeline-progress">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-4 h-4 text-syncchain-accent" />
              <span className="text-sm font-medium text-syncchain-text">Auto-Ingestion Pipeline</span>
              <Loader2 className="w-3 h-3 animate-spin text-syncchain-accent ml-auto" />
            </div>
            <div className="space-y-2">
              {[
                { key: 'recording_metadata_resolved', label: 'Resolving recording metadata' },
                { key: 'credits_resolved', label: 'Fetching credits' },
                { key: 'publishing_resolved', label: 'Resolving publishing data' },
                { key: 'split_editor_prefilled', label: 'Populating split editor' },
              ].map((stage, idx) => {
                const stages = ['recording_metadata_resolved', 'credits_resolved', 'publishing_resolved', 'split_editor_prefilled'];
                const currentIdx = stages.indexOf(search.pipeline_status);
                const stageIdx = stages.indexOf(stage.key);
                const isDone = stageIdx < currentIdx;
                const isCurrent = stageIdx === currentIdx;
                return (
                  <div key={stage.key} className="flex items-center gap-3" data-testid={`pipeline-stage-${stage.key}`}>
                    {isDone ? (
                      <CheckCircle2 className="w-4 h-4 text-syncchain-success shrink-0" />
                    ) : isCurrent ? (
                      <Loader2 className="w-4 h-4 animate-spin text-syncchain-accent shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border border-white/20 shrink-0" />
                    )}
                    <span className={`text-sm ${isDone ? 'text-syncchain-success' : isCurrent ? 'text-syncchain-text' : 'text-syncchain-text-muted'}`}>
                      {stage.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manual Split Verification Badge */}
      {search.pipeline_status === 'manual_split_verification_required' && (
        <Card className="bg-amber-500/5 border-amber-500/20" data-testid="manual-split-verification-banner">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-300">Manual Split Verification Required</p>
                <p className="text-xs text-amber-300/70 mt-1">
                  Writers and publishers have been auto-populated. Split percentages need manual review in the Splits tab.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="recording" className="w-full">
        <TabsList className="bg-syncchain-surface mb-6 p-1">
          <TabsTrigger value="recording" className="data-[state=active]:bg-syncchain-accent data-[state=active]:text-white">
            <Disc className="w-4 h-4 mr-2" />
            Recording
          </TabsTrigger>
          <TabsTrigger value="publishing" className="data-[state=active]:bg-syncchain-accent data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />
            Publishing
          </TabsTrigger>
          <TabsTrigger value="splits" className="data-[state=active]:bg-syncchain-accent data-[state=active]:text-white">
            <Users className="w-4 h-4 mr-2" />
            Splits
          </TabsTrigger>
        </TabsList>

        {/* Recording Tab */}
        <TabsContent value="recording">
          <Card className="bg-syncchain-paper border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg text-syncchain-text flex items-center gap-2">
                <Music2 className="w-5 h-5 text-syncchain-accent" />
                Recording Metadata
              </CardTitle>
              {!editingRecording ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={startEditingRecording}
                  className="border-syncchain-accent/30 text-syncchain-accent hover:bg-syncchain-accent/10"
                  data-testid="edit-recording-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingRecording(false)}
                    className="border-white/20"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveRecordingMetadata}
                    disabled={savingRecording}
                    className="bg-syncchain-success hover:bg-syncchain-success/80"
                    data-testid="save-recording-btn"
                  >
                    {savingRecording ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-1" />
                    )}
                    Save
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {editingRecording ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted">Title</Label>
                      <Input
                        value={editedRecording.title || ''}
                        onChange={(e) => setEditedRecording(prev => ({ ...prev, title: e.target.value }))}
                        className="bg-syncchain-surface border-white/20 text-syncchain-text"
                        data-testid="recording-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted">Artist</Label>
                      <Input
                        value={editedRecording.artist || ''}
                        onChange={(e) => setEditedRecording(prev => ({ ...prev, artist: e.target.value }))}
                        className="bg-syncchain-surface border-white/20 text-syncchain-text"
                        data-testid="recording-artist-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted">ISRC</Label>
                      <Input
                        value={editedRecording.isrc || ''}
                        onChange={(e) => setEditedRecording(prev => ({ ...prev, isrc: e.target.value.toUpperCase() }))}
                        className="bg-syncchain-surface border-white/20 text-syncchain-text font-mono"
                        placeholder="e.g., USRC12345678"
                        data-testid="recording-isrc-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted">UPC</Label>
                      <Input
                        value={editedRecording.upc || ''}
                        onChange={(e) => setEditedRecording(prev => ({ ...prev, upc: e.target.value }))}
                        className="bg-syncchain-surface border-white/20 text-syncchain-text font-mono"
                        placeholder="e.g., 012345678901"
                        data-testid="recording-upc-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted">Release Date</Label>
                      <Input
                        value={editedRecording.releaseDate || ''}
                        onChange={(e) => setEditedRecording(prev => ({ ...prev, releaseDate: e.target.value }))}
                        className="bg-syncchain-surface border-white/20 text-syncchain-text"
                        placeholder="e.g., 2024-01-15"
                        data-testid="recording-releasedate-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted">Release / Album</Label>
                      <Input
                        value={editedRecording.release || ''}
                        onChange={(e) => setEditedRecording(prev => ({ ...prev, release: e.target.value }))}
                        className="bg-syncchain-surface border-white/20 text-syncchain-text"
                        data-testid="recording-release-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted">Label</Label>
                      <Input
                        value={editedRecording.label || ''}
                        onChange={(e) => setEditedRecording(prev => ({ ...prev, label: e.target.value }))}
                        className="bg-syncchain-surface border-white/20 text-syncchain-text"
                        data-testid="recording-label-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted">℗ Sound Recording Owner</Label>
                      <Input
                        value={editedRecording.phonographicCopyright || ''}
                        onChange={(e) => setEditedRecording(prev => ({ ...prev, phonographicCopyright: e.target.value }))}
                        className="bg-syncchain-surface border-white/20 text-syncchain-text"
                        data-testid="recording-copyright-input"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex gap-6">
                  {/* Album Artwork - Option B: Large visual anchor */}
                  <div className="shrink-0">
                    <div className="w-32 h-32 rounded-lg bg-syncchain-surface overflow-hidden shadow-lg">
                      {recording.imageUrl ? (
                        <img 
                          src={recording.imageUrl} 
                          alt={`${recording.title} artwork`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-full h-full items-center justify-center bg-gradient-to-br from-syncchain-accent/20 to-syncchain-accent/5 ${recording.imageUrl ? 'hidden' : 'flex'}`}
                      >
                        <Music2 className="w-12 h-12 text-syncchain-accent/50" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Metadata Grid */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <MetadataRow label="Title" value={recording.title} />
                      <MetadataRow label="Artist" value={recording.artist} />
                      <MetadataRow label="ISRC" value={recording.isrc} mono copyable onCopy={copyToClipboard} />
                      <MetadataRow label="UPC" value={recording.upc} mono copyable onCopy={copyToClipboard} />
                      <MetadataRow label="Duration" value={formatDuration(recording.durationMs)} />
                    </div>
                    <div className="space-y-4">
                      <MetadataRow label="Release Date" value={recording.releaseDate} />
                      <MetadataRow label="Release / Album" value={recording.release} />
                      <MetadataRow label="Label" value={recording.label} />
                      <MetadataRow label="℗ Sound Recording Owner" value={recording.phonographicCopyright || recording.soundRecordingOwnerHint} />
                      <MetadataRow label="Explicit" value={recording.explicit ? "Yes" : "No"} />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Credits Section */}
          <Card className="bg-syncchain-paper border-white/10 mt-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg text-syncchain-text flex items-center gap-2">
                <Users className="w-5 h-5 text-syncchain-accent" />
                Recording Credits
              </CardTitle>
              <div className="flex gap-2">
                {!editingCredits ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={startEditingCredits}
                      className="border-syncchain-accent/30 text-syncchain-accent hover:bg-syncchain-accent/10"
                      data-testid="edit-credits-btn"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={fetchCredits}
                      disabled={fetchingCredits}
                      variant="outline"
                      size="sm"
                      className="border-syncchain-accent/30 text-syncchain-accent hover:bg-syncchain-accent/10"
                      data-testid="fetch-credits-btn"
                    >
                      {fetchingCredits ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Fetching...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Re-fetch Credits
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingCredits(false)}
                      className="border-white/20"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={saveCredits}
                      disabled={savingCredits}
                      className="bg-syncchain-success hover:bg-syncchain-success/80"
                      data-testid="save-credits-btn"
                    >
                      {savingCredits ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-1" />
                      )}
                      Save
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingCredits ? (
                <div className="space-y-6">
                  {/* Add New Credit Entry */}
                  <div className="p-4 bg-syncchain-surface/50 rounded-lg border border-white/10">
                    <h4 className="text-sm font-mono uppercase tracking-wider text-syncchain-text-secondary mb-3">
                      Add New Credit
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <Select
                        value={newCreditEntry.category}
                        onValueChange={(value) => setNewCreditEntry(prev => ({ ...prev, category: value }))}
                      >
                        <SelectTrigger className="bg-syncchain-bg border-white/20 text-syncchain-text">
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="writers">Writer/Composer</SelectItem>
                          <SelectItem value="producers">Producer</SelectItem>
                          <SelectItem value="engineers">Engineer</SelectItem>
                          <SelectItem value="musicians">Musician</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        value={newCreditEntry.name}
                        onChange={(e) => setNewCreditEntry(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Name *"
                        className="bg-syncchain-bg border-white/20 text-syncchain-text"
                        data-testid="new-credit-name"
                      />
                      <Input
                        value={newCreditEntry.role}
                        onChange={(e) => setNewCreditEntry(prev => ({ ...prev, role: e.target.value }))}
                        placeholder="Role (optional)"
                        className="bg-syncchain-bg border-white/20 text-syncchain-text"
                        data-testid="new-credit-role"
                      />
                      <Button
                        onClick={addCreditEntry}
                        className="bg-syncchain-accent hover:bg-syncchain-accent/80"
                        data-testid="add-credit-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  </div>

                  {/* Editable Writers */}
                  <div>
                    <h4 className="text-sm font-mono uppercase tracking-wider text-syncchain-text-secondary mb-3">
                      Writers/Composers ({editedCredits.writers?.length || 0})
                    </h4>
                    <div className="space-y-2">
                      {editedCredits.writers?.map((w, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-syncchain-surface/30 rounded-lg">
                          <Input
                            value={w.name}
                            onChange={(e) => updateCreditEntry('writers', idx, 'name', e.target.value)}
                            className="flex-1 bg-syncchain-bg border-white/20 text-syncchain-text h-9"
                            placeholder="Name"
                          />
                          <Input
                            value={w.role}
                            onChange={(e) => updateCreditEntry('writers', idx, 'role', e.target.value)}
                            className="w-32 bg-syncchain-bg border-white/20 text-syncchain-text h-9"
                            placeholder="Role"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeCreditEntry('writers', idx)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-9 w-9"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {(!editedCredits.writers || editedCredits.writers.length === 0) && (
                        <p className="text-syncchain-text-muted text-sm py-2">No writers. Add one above.</p>
                      )}
                    </div>
                  </div>

                  {/* Editable Producers */}
                  <div>
                    <h4 className="text-sm font-mono uppercase tracking-wider text-syncchain-text-secondary mb-3">
                      Producers ({editedCredits.producers?.length || 0})
                    </h4>
                    <div className="space-y-2">
                      {editedCredits.producers?.map((p, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-syncchain-surface/30 rounded-lg">
                          <Input
                            value={p.name}
                            onChange={(e) => updateCreditEntry('producers', idx, 'name', e.target.value)}
                            className="flex-1 bg-syncchain-bg border-white/20 text-syncchain-text h-9"
                            placeholder="Name"
                          />
                          <Input
                            value={p.role}
                            onChange={(e) => updateCreditEntry('producers', idx, 'role', e.target.value)}
                            className="w-32 bg-syncchain-bg border-white/20 text-syncchain-text h-9"
                            placeholder="Role"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeCreditEntry('producers', idx)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-9 w-9"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {(!editedCredits.producers || editedCredits.producers.length === 0) && (
                        <p className="text-syncchain-text-muted text-sm py-2">No producers. Add one above.</p>
                      )}
                    </div>
                  </div>

                  {/* Editable Engineers */}
                  <div>
                    <h4 className="text-sm font-mono uppercase tracking-wider text-syncchain-text-secondary mb-3">
                      Engineers ({editedCredits.engineers?.length || 0})
                    </h4>
                    <div className="space-y-2">
                      {editedCredits.engineers?.map((e, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-syncchain-surface/30 rounded-lg">
                          <Input
                            value={e.name}
                            onChange={(ev) => updateCreditEntry('engineers', idx, 'name', ev.target.value)}
                            className="flex-1 bg-syncchain-bg border-white/20 text-syncchain-text h-9"
                            placeholder="Name"
                          />
                          <Input
                            value={e.role}
                            onChange={(ev) => updateCreditEntry('engineers', idx, 'role', ev.target.value)}
                            className="w-32 bg-syncchain-bg border-white/20 text-syncchain-text h-9"
                            placeholder="Role"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeCreditEntry('engineers', idx)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-9 w-9"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {(!editedCredits.engineers || editedCredits.engineers.length === 0) && (
                        <p className="text-syncchain-text-muted text-sm py-2">No engineers. Add one above.</p>
                      )}
                    </div>
                  </div>

                  {/* Editable Musicians */}
                  <div>
                    <h4 className="text-sm font-mono uppercase tracking-wider text-syncchain-text-secondary mb-3">
                      Musicians ({editedCredits.musicians?.length || 0})
                    </h4>
                    <div className="space-y-2">
                      {editedCredits.musicians?.map((m, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-syncchain-surface/30 rounded-lg">
                          <Input
                            value={m.name}
                            onChange={(e) => updateCreditEntry('musicians', idx, 'name', e.target.value)}
                            className="flex-1 bg-syncchain-bg border-white/20 text-syncchain-text h-9"
                            placeholder="Name"
                          />
                          <Input
                            value={m.role}
                            onChange={(e) => updateCreditEntry('musicians', idx, 'role', e.target.value)}
                            className="w-32 bg-syncchain-bg border-white/20 text-syncchain-text h-9"
                            placeholder="Role"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeCreditEntry('musicians', idx)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-9 w-9"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {(!editedCredits.musicians || editedCredits.musicians.length === 0) && (
                        <p className="text-syncchain-text-muted text-sm py-2">No musicians. Add one above.</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (creditsData?.writers?.length > 0 || creditsData?.producers?.length > 0 || creditsData?.engineers?.length > 0 || creditsData?.musicians?.length > 0) ? (
                <div className="space-y-6">
                  {/* Writers/Composers */}
                  {creditsData?.writers?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-mono uppercase tracking-wider text-syncchain-text-secondary mb-3">
                        Writers/Composers ({creditsData.writers.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {creditsData.writers.map((w, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline" 
                            className="bg-syncchain-success/10 text-syncchain-success border-syncchain-success/30 py-1.5 px-3"
                          >
                            <span className="font-medium">{w.name}</span>
                            <span className="text-xs ml-2 opacity-70">({w.role})</span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Producers */}
                  {creditsData?.producers?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-mono uppercase tracking-wider text-syncchain-text-secondary mb-3">
                        Producers ({creditsData.producers.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {creditsData.producers.map((p, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline" 
                            className="bg-syncchain-accent/10 text-syncchain-accent border-syncchain-accent/30 py-1.5 px-3"
                          >
                            <span className="font-medium">{p.name}</span>
                            <span className="text-xs ml-2 opacity-70">({p.role})</span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Engineers */}
                  {creditsData?.engineers?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-mono uppercase tracking-wider text-syncchain-text-secondary mb-3">
                        Engineers ({creditsData.engineers.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {creditsData.engineers.map((e, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline" 
                            className="bg-syncchain-info/10 text-syncchain-info border-syncchain-info/30 py-1.5 px-3"
                          >
                            <span className="font-medium">{e.name}</span>
                            <span className="text-xs ml-2 opacity-70">({e.role})</span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Musicians */}
                  {creditsData?.musicians?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-mono uppercase tracking-wider text-syncchain-text-secondary mb-3">
                        Musicians ({creditsData.musicians.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {creditsData.musicians.map((m, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline" 
                            className="bg-white/5 text-syncchain-text-secondary border-white/10 py-1.5 px-3"
                          >
                            <span className="font-medium">{m.name}</span>
                            <span className="text-xs ml-2 opacity-70">({m.role})</span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-syncchain-text-muted pt-2">
                    Credits sourced from verified industry databases. Click &quot;Edit&quot; to make corrections.
                  </p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Database className="w-10 h-10 mx-auto mb-3 text-syncchain-text-muted" />
                  <p className="text-syncchain-text-muted text-sm">
                    {search.pipeline_status && search.status === 'searching' 
                      ? 'Credits are being fetched automatically...'
                      : 'No credits found. Click "Re-fetch Credits" to retry, or "Edit" to add manually.'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Publishing Tab */}
        <TabsContent value="publishing">
          <Card className="bg-syncchain-paper border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-syncchain-text flex items-center gap-2">
                <FileText className="w-5 h-5 text-syncchain-accent" />
                Publishing Metadata
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* BMI Status Message */}
              {publishing.bmi_status && ['skipped_browser_unavailable', 'skipped_blocked', 'failed'].includes(publishing.bmi_status) && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-200 text-sm font-medium">Publishing Lookup Unavailable</p>
                      <p className="text-amber-200/70 text-sm mt-1">
                        {publishing.user_message || 'Publishing lookup temporarily unavailable. Recording metadata and credits were still processed.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <MetadataRow label="Work Title" value={publishing.workTitle || recording.title} />
                <MetadataRow label="Primary ISWC" value={publishing.iswc} mono copyable onCopy={copyToClipboard} />
              </div>
              
              {/* All ISWCs by Country/PRO */}
              {proData?.allIswcs?.length > 0 && (
                <>
                  <Separator className="bg-white/10" />
                  <div>
                    <h4 className="text-sm font-mono uppercase tracking-wider text-syncchain-text-secondary mb-3 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      ISWC Codes by PRO ({proData.allIswcs.length})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {proData.allIswcs.map((iswc, idx) => (
                        <div 
                          key={idx} 
                          className="p-3 bg-syncchain-surface/50 rounded-lg border border-white/5 hover:border-syncchain-accent/30 transition-colors cursor-pointer group"
                          onClick={() => copyToClipboard(iswc.code, `ISWC (${iswc.source})`)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Badge variant="outline" className="bg-syncchain-accent/10 text-syncchain-accent border-syncchain-accent/30 text-xs">
                              {iswc.country}
                            </Badge>
                            <Copy className="w-3 h-3 text-syncchain-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <p className="font-mono text-sm text-syncchain-text">{iswc.code}</p>
                          <p className="text-xs text-syncchain-text-muted mt-1">{iswc.source}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
              
              <Separator className="bg-white/10" />
              
              <div>
                <h4 className="text-sm font-mono uppercase tracking-wider text-syncchain-text-secondary mb-3">
                  Writers ({displayWriters.length})
                </h4>
                {displayWriters.length > 0 ? (
                  <div className="space-y-2">
                    {displayWriters.map((writer, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-syncchain-surface/50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-syncchain-text font-medium">{writer.name}</p>
                            {writer.source && (
                              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${writer.source === 'PRS' ? 'bg-blue-500/10 text-blue-400' : 'bg-amber-500/10 text-amber-400'}`}>
                                {writer.source}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            {writer.ipi && (
                              <p className="text-xs font-mono text-syncchain-text-muted">IPI: {writer.ipi}</p>
                            )}
                            {writer.source === 'PRS' || writer.performing_share != null ? (
                              <>
                                <span className="text-xs text-blue-400" data-testid={`writer-perf-share-${idx}`}>
                                  Perf: {writer.performing_share ?? 0}%{(writer.performing_society || writer.prs_society) ? ` (${writer.performing_society || writer.prs_society})` : ''}
                                </span>
                                <span className="text-xs text-purple-400" data-testid={`writer-mech-share-${idx}`}>
                                  Mech: {writer.mechanical_share ?? 0}%{(writer.mechanical_society) ? ` (${writer.mechanical_society})` : ''}
                                </span>
                              </>
                            ) : (
                              <span className="text-xs text-syncchain-text-muted italic" data-testid={`writer-no-share-${idx}`}>
                                Shares not available ({writer.source || 'BMI'})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-syncchain-text-muted text-sm">No writers found. Add via Splits tab.</p>
                )}
              </div>

              <div>
                <h4 className="text-sm font-mono uppercase tracking-wider text-syncchain-text-secondary mb-3">
                  Publishers ({displayPublishers.length})
                </h4>
                {displayPublishers.length > 0 ? (
                  <div className="space-y-2">
                    {displayPublishers.map((pub, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-syncchain-surface/50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-syncchain-text font-medium">{pub.name}</p>
                            {pub.source && (
                              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${pub.source === 'PRS' ? 'bg-blue-500/10 text-blue-400' : 'bg-amber-500/10 text-amber-400'}`}>
                                {pub.source}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            {pub.ipi && (
                              <p className="text-xs font-mono text-syncchain-text-muted">IPI: {pub.ipi}</p>
                            )}
                            {pub.source === 'PRS' || pub.performing_share != null ? (
                              <>
                                <span className="text-xs text-blue-400" data-testid={`pub-perf-share-${idx}`}>
                                  Perf: {pub.performing_share ?? 0}%{pub.performing_society ? ` (${pub.performing_society})` : ''}
                                </span>
                                <span className="text-xs text-purple-400" data-testid={`pub-mech-share-${idx}`}>
                                  Mech: {pub.mechanical_share ?? 0}%{pub.mechanical_society ? ` (${pub.mechanical_society})` : ''}
                                </span>
                              </>
                            ) : (
                              <span className="text-xs text-syncchain-text-muted italic" data-testid={`pub-no-share-${idx}`}>
                                Shares not available ({pub.source || 'BMI'})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-syncchain-text-muted text-sm">No publishers found. Add via Splits tab.</p>
                )}
              </div>
              
              {/* Share Totals Summary */}
              {(displayWriters.length > 0 || displayPublishers.length > 0) && (
                <>
                  <Separator className="bg-white/10" />
                  <div className="p-4 bg-syncchain-surface/30 rounded-lg border border-white/5">
                    <h4 className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted mb-3">Share Totals (PRS Authority)</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-syncchain-text-muted">Writers Performing</p>
                        <p className="text-sm font-mono text-blue-400">
                          {displayWriters.filter(w => w.source === 'PRS' || w.performing_share != null).reduce((sum, w) => sum + (parseFloat(w.performing_share) || 0), 0).toFixed(2)}%
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-syncchain-text-muted">Writers Mechanical</p>
                        <p className="text-sm font-mono text-purple-400">
                          {displayWriters.filter(w => w.source === 'PRS' || w.mechanical_share != null).reduce((sum, w) => sum + (parseFloat(w.mechanical_share) || 0), 0).toFixed(2)}%
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-syncchain-text-muted">Publishers Performing</p>
                        <p className="text-sm font-mono text-blue-400">
                          {displayPublishers.filter(p => p.source === 'PRS' || p.performing_share != null).reduce((sum, p) => sum + (parseFloat(p.performing_share) || 0), 0).toFixed(2)}%
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-syncchain-text-muted">Publishers Mechanical</p>
                        <p className="text-sm font-mono text-purple-400">
                          {displayPublishers.filter(p => p.source === 'PRS' || p.mechanical_share != null).reduce((sum, p) => sum + (parseFloat(p.mechanical_share) || 0), 0).toFixed(2)}%
                        </p>
                      </div>
                    </div>
                    <Separator className="bg-white/5 my-3" />
                    {(() => {
                      const prsWriters = displayWriters.filter(w => w.source === 'PRS' || w.performing_share != null);
                      const prsPublishers = displayPublishers.filter(p => p.source === 'PRS' || p.performing_share != null);
                      const totalPerf = prsWriters.reduce((s,w) => s + (parseFloat(w.performing_share)||0), 0) + prsPublishers.reduce((s,p) => s + (parseFloat(p.performing_share)||0), 0);
                      const totalMech = prsWriters.reduce((s,w) => s + (parseFloat(w.mechanical_share)||0), 0) + prsPublishers.reduce((s,p) => s + (parseFloat(p.mechanical_share)||0), 0);
                      const combined = totalPerf + totalMech;
                      return (
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs text-syncchain-text-muted">Total Performing</p>
                            <p className={`text-sm font-mono font-medium ${Math.abs(totalPerf - 100) < 1 ? 'text-blue-300' : 'text-syncchain-warning'}`}>
                              {totalPerf.toFixed(2)}%
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-syncchain-text-muted">Total Mechanical</p>
                            <p className={`text-sm font-mono font-medium ${Math.abs(totalMech - 100) < 1 ? 'text-purple-300' : 'text-syncchain-warning'}`}>
                              {totalMech.toFixed(2)}%
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-syncchain-text-muted">Combined</p>
                            <p className={`text-sm font-mono font-medium ${Math.abs(combined - 200) < 2 ? 'text-syncchain-success' : 'text-syncchain-warning'}`}>
                              {combined.toFixed(2)}%
                              <span className="text-xs text-syncchain-text-muted ml-1">/ 200%</span>
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                    {displayWriters.some(w => w.source !== 'PRS' && w.performing_share == null) || displayPublishers.some(p => p.source !== 'PRS' && p.performing_share == null) ? (
                      <p className="text-xs text-syncchain-text-muted mt-3 italic">
                        * BMI entries excluded from totals — BMI does not provide share breakdowns. PRS is the authority for Performing/Mechanical shares.
                      </p>
                    ) : null}
                  </div>
                </>
              )}
              
              {/* Contact Information Section */}
              {(proData?.contactInfo?.length > 0 || publishing.publisher_contacts?.length > 0) && (
                <>
                  <Separator className="bg-white/10" />
                  <div>
                    <h4 className="text-sm font-mono uppercase tracking-wider text-syncchain-text-secondary mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Publisher Contact Info ({(proData?.contactInfo || publishing.publisher_contacts || []).length})
                    </h4>
                    <div className="space-y-3">
                      {(proData?.contactInfo?.length > 0 ? proData.contactInfo : publishing.publisher_contacts || []).map((contact, idx) => (
                        <div key={idx} className="p-3 bg-syncchain-surface/50 rounded-lg border border-white/5">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-syncchain-text font-medium flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-syncchain-accent" />
                                {contact.publisher_name || contact.name}
                              </p>
                              {(contact.publisher_ipi || contact.ipi) && (
                                <p className="text-xs font-mono text-syncchain-text-muted mt-1 ml-6">
                                  IPI: {contact.publisher_ipi || contact.ipi}
                                </p>
                              )}
                              {contact.administered_by && (
                                <p className="text-sm text-syncchain-text-secondary mt-2 ml-6 flex items-center gap-2">
                                  <ArrowRight className="w-3 h-3 text-syncchain-accent shrink-0" />
                                  <span>Administered by <span className="text-syncchain-text font-medium">{contact.administered_by}</span></span>
                                </p>
                              )}
                              {contact.address && (
                                <p className="text-sm text-syncchain-text-secondary mt-2 ml-6 flex items-start gap-2">
                                  <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-syncchain-accent" />
                                  {contact.address}
                                </p>
                              )}
                              {(contact.contact_email || contact.email) && (
                                <p className="text-sm text-syncchain-text-secondary mt-1 ml-6 flex items-center gap-2">
                                  <Mail className="w-4 h-4 text-syncchain-accent" />
                                  <a href={`mailto:${contact.contact_email || contact.email}`} className="text-syncchain-accent hover:underline">
                                    {contact.contact_email || contact.email}
                                  </a>
                                </p>
                              )}
                              {contact.phone && (
                                <p className="text-sm text-syncchain-text-secondary mt-1 ml-6 flex items-center gap-2">
                                  <Globe className="w-4 h-4 text-syncchain-accent" />
                                  {contact.phone}
                                </p>
                              )}
                              {contact.website && (
                                <p className="text-sm text-syncchain-text-secondary mt-1 ml-6 flex items-center gap-2">
                                  <Globe className="w-4 h-4 text-syncchain-accent" />
                                  <a href={contact.website} target="_blank" rel="noopener noreferrer" className="text-syncchain-accent hover:underline">
                                    {contact.website}
                                  </a>
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Splits Tab */}
        <TabsContent value="splits">
          {/* PRO Share Data (UK) Enrichment Status & Trigger */}
          <Card className="bg-gradient-to-r from-syncchain-accent/20 to-syncchain-paper border-syncchain-accent/30 mb-4">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-syncchain-accent/20 flex items-center justify-center shrink-0 mt-1">
                  <Sparkles className="w-5 h-5 text-syncchain-accent" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div>
                      <p className="font-medium text-syncchain-text">Rights Data</p>
                      {(proData?.writers?.length > 0 || proData?.publishers?.length > 0) && (
                        <p className="text-xs text-syncchain-success mt-0.5">
                          Auto-populated: {proData?.writers?.length || 0} writers, {proData?.publishers?.length || 0} publishers
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => fetchProData(false)}
                        disabled={fetchingPro}
                        variant="outline"
                        className="border-syncchain-accent/50 text-syncchain-accent hover:bg-syncchain-accent/10"
                        size="sm"
                        data-testid="fetch-pro-data-btn"
                      >
                        {fetchingPro ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Fetching...
                          </>
                        ) : (
                          <>
                            <Target className="w-4 h-4 mr-2" />
                            Re-fetch
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => fetchProData(true)}
                        disabled={fetchingPro}
                        variant="outline"
                        className="border-white/10 text-syncchain-text-muted hover:bg-white/5"
                        size="sm"
                        title="Force refresh - bypass 24-hour cache"
                        data-testid="refresh-pro-data-btn"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-syncchain-text-secondary">
                    Writers and publishers are auto-fetched during search. Use <strong>Re-fetch</strong> to retry, or the refresh button to bypass cache.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* PRO Share Data (UK) Enrichment Status & Trigger */}
          <Card className="bg-gradient-to-r from-blue-900/20 to-syncchain-paper border-blue-500/20 mb-4">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0 mt-1">
                  <Globe className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div>
                      <p className="font-medium text-syncchain-text">PRO Share Data (UK)</p>
                      {search?.results_json?.publishing?.prs_status === 'prs_completed' && (
                        <p className="text-xs text-green-400 mt-0.5">
                          Enrichment complete - performing & mechanical shares populated
                        </p>
                      )}
                      {search?.results_json?.publishing?.prs_status === 'prs_processing' && (
                        <p className="text-xs text-yellow-400 mt-0.5 animate-pulse">
                          Enrichment in progress...
                        </p>
                      )}
                      {search?.results_json?.publishing?.prs_status === 'prs_queued' && (
                        <p className="text-xs text-blue-400 mt-0.5">
                          Enrichment queued
                        </p>
                      )}
                      {search?.results_json?.publishing?.prs_status === 'prs_failed' && (
                        <p className="text-xs text-red-400 mt-0.5">
                          Enrichment failed - click to retry
                        </p>
                      )}
                      {search?.results_json?.publishing?.prs_status === 'no_results' && (
                        <div className="mt-0.5">
                          <p className="text-xs text-amber-400">
                            No confident match found on PRS for this work
                          </p>
                          {(!creditsData?.writers || creditsData.writers.length === 0) && (
                            <p className="text-xs text-syncchain-accent mt-1" data-testid="prs-hint-add-writer">
                              No PRO shares found. Try adding a writer name in Recording Credits, then click Enrich.
                            </p>
                          )}
                        </div>
                      )}
                      {(!search?.results_json?.publishing?.prs_status || search?.results_json?.publishing?.prs_status === 'prs_skipped') && (
                        <p className="text-xs text-syncchain-text-muted mt-0.5">
                          Fetch writer/publisher share percentages from UK PRO
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={fetchPrsData}
                      disabled={fetchingPrs}
                      variant="outline"
                      className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      size="sm"
                      data-testid="fetch-prs-data-btn"
                    >
                      {fetchingPrs ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : search?.results_json?.publishing?.prs_status === 'prs_completed' ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Re-fetch
                        </>
                      ) : (
                        <>
                          <Globe className="w-4 h-4 mr-2" />
                          Enrich
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-syncchain-text-secondary">
                    Performing & mechanical share percentages, society affiliations, and CAE/IPI numbers.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Filter Stats Card */}
          {proData?.filterStats && (
            <Card className="bg-syncchain-surface/50 border-white/10 mb-4">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Filter className="w-5 h-5 text-syncchain-accent" />
                  <span className="font-medium text-syncchain-text">
                    {proData.filterStats.filter_mode === 'pro_direct' ? 'PRO Database Results' : 'Smart Filter Results'}
                  </span>
                </div>
                {proData.filterStats.filter_mode === 'pro_direct' ? (
                  // PRO database mode stats
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-syncchain-bg rounded-lg">
                      <p className="text-2xl font-bold text-syncchain-accent">{proData.filterStats.total_writers || 0}</p>
                      <p className="text-xs text-syncchain-text-muted">Writers Found</p>
                    </div>
                    <div className="text-center p-3 bg-syncchain-bg rounded-lg">
                      <p className="text-2xl font-bold text-syncchain-info">{proData.filterStats.main_publishers_count || 0}</p>
                      <p className="text-xs text-syncchain-text-muted">Main Publishers</p>
                    </div>
                    <div className="text-center p-3 bg-syncchain-bg rounded-lg">
                      <p className="text-2xl font-bold text-syncchain-warning">{proData.filterStats.sub_publishers_count || 0}</p>
                      <p className="text-xs text-syncchain-text-muted">Sub-Publishers</p>
                    </div>
                    <div className="text-center p-3 bg-syncchain-bg rounded-lg">
                      <p className="text-2xl font-bold text-syncchain-success">{proData.filterStats.total_publishers || 0}</p>
                      <p className="text-xs text-syncchain-text-muted">Total Publishers</p>
                    </div>
                  </div>
                ) : (
                  // Multi-PRO mode stats (legacy)
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-syncchain-bg rounded-lg">
                      <p className="text-2xl font-bold text-syncchain-accent">{proData.filterStats.verified_names_count}</p>
                      <p className="text-xs text-syncchain-text-muted">Verified Credits</p>
                    </div>
                    <div className="text-center p-3 bg-syncchain-bg rounded-lg">
                      <p className="text-2xl font-bold text-syncchain-success">{proData.filterStats.matched_writers_count}</p>
                      <p className="text-xs text-syncchain-text-muted">Name Matched</p>
                    </div>
                    <div className="text-center p-3 bg-syncchain-bg rounded-lg">
                      <p className="text-2xl font-bold text-syncchain-info">{proData.filterStats.unique_writers_count || (proData.filterStats.matched_writers_count + proData.filterStats.unmatched_writers_count)}</p>
                      <p className="text-xs text-syncchain-text-muted">Unique (by IPI)</p>
                    </div>
                    <div className="text-center p-3 bg-syncchain-bg rounded-lg">
                      <p className="text-2xl font-bold text-syncchain-text-secondary">{proData.filterStats.duplicates_removed || 0}</p>
                      <p className="text-xs text-syncchain-text-muted">Dupes Removed</p>
                    </div>
                  </div>
                )}
                {proData.filterStats.filter_mode === 'pro_direct' && (
                  <div className="mt-3 p-2 bg-syncchain-success/10 rounded-lg flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-syncchain-success" />
                    <p className="text-xs text-syncchain-success">
                      Rights data resolved successfully.
                    </p>
                  </div>
                )}
                {proData.filterStats.has_verification && (
                  <div className="mt-3 p-2 bg-syncchain-success/10 rounded-lg flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-syncchain-success" />
                    <p className="text-xs text-syncchain-success">
                      Verification found! Showing all {proData.filterStats.unique_writers_count || 'unique'} writers (deduplicated by IPI). 
                      {proData.filterStats.unmatched_writers_count > 0 && ` ${proData.filterStats.unmatched_writers_count} may use stage names or alternate spellings.`}
                    </p>
                  </div>
                )}
                {proData.filterStats.warning && (
                  <div className="mt-3 p-2 bg-syncchain-warning/10 rounded-lg flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-syncchain-warning" />
                    <p className="text-xs text-syncchain-warning">{proData.filterStats.warning}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Quick Add from Evidence */}
          <QuickAddPanel 
            onAddWriter={(writer) => {
              setProData(prev => ({
                ...prev,
                writers: [...(prev?.writers || []), writer]
              }));
            }}
            onAddPublisher={(publisher) => {
              setProData(prev => ({
                ...prev,
                publishers: [...(prev?.publishers || []), publisher]
              }));
            }}
          />

          {/* Rights Data Results */}
          {proData && (proData.writers?.length > 0 || proData.publishers?.length > 0 || proData.mainPublishers?.length > 0 || proData.unmatchedWriters?.length > 0) && (
            <Card className="bg-syncchain-paper border-syncchain-accent/30 mb-4 animate-slide-up">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg text-syncchain-text flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-syncchain-success" />
                    Select Data to Import
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-syncchain-text-muted">
                      {selectedWriters.size + selectedPublishers.size} selected
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {proData.writers?.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted flex items-center gap-2">
                        <CheckCircle2 className="w-3 h-3 text-syncchain-success" />
                        Writers ({proData.writers.length})
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6 px-2"
                          onClick={() => {
                            const allWriterKeys = proData.writers.map((_, idx) => `writer-${idx}`);
                            setSelectedWriters(new Set(allWriterKeys));
                          }}
                        >
                          Select All
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6 px-2"
                          onClick={() => setSelectedWriters(new Set())}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {proData.writers.map((writer, idx) => {
                        const key = `writer-${idx}`;
                        const isSelected = selectedWriters.has(key);
                        return (
                          <div 
                            key={idx} 
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                              isSelected 
                                ? 'bg-syncchain-success/10 border-syncchain-success/40' 
                                : 'bg-syncchain-surface/50 border-syncchain-success/20 hover:border-syncchain-success/40'
                            }`}
                            onClick={() => {
                              const newSelected = new Set(selectedWriters);
                              if (isSelected) {
                                newSelected.delete(key);
                              } else {
                                newSelected.add(key);
                              }
                              setSelectedWriters(newSelected);
                            }}
                          >
                            <Checkbox 
                              checked={isSelected}
                              className="data-[state=checked]:bg-syncchain-success data-[state=checked]:border-syncchain-success"
                            />
                            <div className="flex-1">
                              <p className="text-syncchain-text font-medium">{writer.name}</p>
                              <div className="flex gap-2 mt-1 flex-wrap">
                                {writer.ipi && (
                                  <span className="text-xs font-mono text-syncchain-text-muted">IPI: {writer.ipi}</span>
                                )}
                                {writer.matched_to && (
                                  <span className="text-xs text-syncchain-success">
                                    Matched to: {writer.matched_to}
                                  </span>
                                )}
                                {writer.performing_share != null && (
                                  <span className="text-xs text-blue-400">
                                    Perf: {writer.performing_share}%{writer.performing_society ? ` (${writer.performing_society})` : writer.prs_society ? ` (${writer.prs_society})` : ''}
                                  </span>
                                )}
                                {writer.mechanical_share != null && (
                                  <span className="text-xs text-purple-400">
                                    Mech: {writer.mechanical_share}%{writer.mechanical_society ? ` (${writer.mechanical_society})` : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Badge variant="outline" className="bg-syncchain-success/10 text-syncchain-success border-syncchain-success/20 text-xs">
                              <Target className="w-3 h-3 mr-1" />
                              verified
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Other Writers (stage names, alternate spellings) */}
                {proData.unmatchedWriters?.length > 0 && (
                  <div className="mb-4">
                    <button
                      onClick={() => setShowUnmatched(!showUnmatched)}
                      className="w-full flex items-center justify-between text-xs font-mono uppercase tracking-wider text-syncchain-text-muted mb-2 p-2 hover:bg-syncchain-surface/30 rounded transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <Users className="w-3 h-3 text-syncchain-info" />
                        Other Writers ({proData.unmatchedWriters.length}) - Stage names / alternate spellings
                      </span>
                      {showUnmatched ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    
                    {showUnmatched && (
                      <div className="space-y-2 animate-slide-up">
                        <p className="text-xs text-syncchain-text-muted p-2 bg-syncchain-info/5 rounded mb-2">
                          These writers have unique IPIs but names didn&apos;t match credits. 
                          They may use stage names (e.g. &quot;Belly&quot; = Ahmad Balshe) or have different name formats in different PROs.
                        </p>
                        {proData.unmatchedWriters.map((writer, idx) => {
                          const key = `unmatched-${idx}`;
                          const isSelected = selectedWriters.has(key);
                          return (
                            <div 
                              key={idx} 
                              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                isSelected 
                                  ? 'bg-syncchain-info/10 border-syncchain-info/40' 
                                  : 'bg-syncchain-surface/30 border-white/10 hover:border-syncchain-info/40'
                              }`}
                              onClick={() => {
                                const newSelected = new Set(selectedWriters);
                                if (isSelected) {
                                  newSelected.delete(key);
                                } else {
                                  newSelected.add(key);
                                }
                                setSelectedWriters(newSelected);
                              }}
                            >
                              <Checkbox 
                                checked={isSelected}
                                className="data-[state=checked]:bg-syncchain-info data-[state=checked]:border-syncchain-info"
                              />
                              <div className="flex-1">
                                <p className="text-syncchain-text-secondary font-medium">{writer.name}</p>
                                <div className="flex gap-2 mt-1">
                                  {writer.ipi && (
                                    <span className="text-xs font-mono text-syncchain-text-muted">IPI: {writer.ipi}</span>
                                  )}
                                  {writer.pro && (
                                    <span className="text-xs text-syncchain-text-muted">PRO: {writer.pro}</span>
                                  )}
                                </div>
                              </div>
                              <Badge variant="outline" className="bg-syncchain-info/10 text-syncchain-info border-syncchain-info/20 text-xs">
                                unique IPI
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Main Publishers Section */}
                {(proData.mainPublishers?.length > 0 || proData.publishers?.length > 0) && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted">
                        Main Publishers ({proData.mainPublishers?.length || proData.publishers?.length || 0})
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6 px-2"
                          onClick={() => {
                            const allPubKeys = (proData.mainPublishers || proData.publishers || []).map((_, idx) => `pub-${idx}`);
                            setSelectedPublishers(new Set(allPubKeys));
                          }}
                        >
                          Select All
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6 px-2"
                          onClick={() => setSelectedPublishers(new Set())}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {(proData.mainPublishers || proData.publishers || []).map((pub, idx) => {
                        const key = `pub-${idx}`;
                        const isSelected = selectedPublishers.has(key);
                        return (
                          <div 
                            key={idx} 
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                              isSelected 
                                ? 'bg-syncchain-info/10 border-syncchain-info/40' 
                                : 'bg-syncchain-surface/50 border-white/10 hover:border-syncchain-info/40'
                            }`}
                            onClick={() => {
                              const newSelected = new Set(selectedPublishers);
                              if (isSelected) {
                                newSelected.delete(key);
                              } else {
                                newSelected.add(key);
                              }
                              setSelectedPublishers(newSelected);
                            }}
                          >
                            <Checkbox 
                              checked={isSelected}
                              className="data-[state=checked]:bg-syncchain-info data-[state=checked]:border-syncchain-info"
                            />
                            <div className="flex-1">
                              <p className="text-syncchain-text font-medium">{pub.name}</p>
                              <div className="flex gap-2 mt-1 flex-wrap">
                                {pub.ipi && (
                                  <span className="text-xs font-mono text-syncchain-text-muted">IPI: {pub.ipi}</span>
                                )}
                                {pub.pro && (
                                  <span className="text-xs font-mono text-syncchain-text-muted">PRO: {pub.pro}</span>
                                )}
                                {pub.performing_share != null && (
                                  <span className="text-xs text-blue-400">
                                    Perf: {pub.performing_share}%{pub.performing_society ? ` (${pub.performing_society})` : pub.prs_society ? ` (${pub.prs_society})` : ''}
                                  </span>
                                )}
                                {pub.mechanical_share != null && (
                                  <span className="text-xs text-purple-400">
                                    Mech: {pub.mechanical_share}%{pub.mechanical_society ? ` (${pub.mechanical_society})` : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Badge variant="outline" className="bg-syncchain-info/10 text-syncchain-info border-syncchain-info/20 text-xs">
                              main publisher
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Sub Publishers Section */}
                {proData.subPublishers?.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted">
                        Sub-Publishers / Admin ({proData.subPublishers.length})
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6 px-2"
                          onClick={() => {
                            const allSubPubKeys = proData.subPublishers.map((_, idx) => `subpub-${idx}`);
                            const newSelected = new Set(selectedPublishers);
                            allSubPubKeys.forEach(k => newSelected.add(k));
                            setSelectedPublishers(newSelected);
                          }}
                        >
                          Select All
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2 ml-4 border-l-2 border-white/5 pl-4">
                      {proData.subPublishers.map((pub, idx) => {
                        const key = `subpub-${idx}`;
                        const isSelected = selectedPublishers.has(key);
                        return (
                          <div 
                            key={idx} 
                            className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-all ${
                              isSelected 
                                ? 'bg-syncchain-warning/10 border-syncchain-warning/40' 
                                : 'bg-syncchain-surface/30 border-white/5 hover:border-syncchain-warning/40'
                            }`}
                            onClick={() => {
                              const newSelected = new Set(selectedPublishers);
                              if (isSelected) {
                                newSelected.delete(key);
                              } else {
                                newSelected.add(key);
                              }
                              setSelectedPublishers(newSelected);
                            }}
                          >
                            <Checkbox 
                              checked={isSelected}
                              className="data-[state=checked]:bg-syncchain-warning data-[state=checked]:border-syncchain-warning"
                            />
                            <div className="flex-1">
                              <p className="text-syncchain-text text-sm">{pub.name}</p>
                              <div className="flex gap-2 mt-1 flex-wrap">
                                {pub.ipi && (
                                  <span className="text-xs font-mono text-syncchain-text-muted">IPI: {pub.ipi}</span>
                                )}
                                {pub.parent_publisher && (
                                  <span className="text-xs text-syncchain-text-muted">→ {pub.parent_publisher}</span>
                                )}
                              </div>
                            </div>
                            <Badge variant="outline" className="bg-syncchain-warning/10 text-syncchain-warning border-syncchain-warning/20 text-xs">
                              sub-pub
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <div className="mt-4 p-3 bg-syncchain-surface/30 rounded-lg flex items-center justify-between">
                  <p className="text-xs text-syncchain-text-muted">
                    Select items above, then click Import to add to Split Editor
                  </p>
                  <Button
                    size="sm"
                    className="bg-syncchain-accent hover:bg-syncchain-accent-hover text-white"
                    disabled={selectedWriters.size + selectedPublishers.size === 0}
                    onClick={() => {
                      // Get selected writers (both verified and unmatched)
                      const writersToImport = [];
                      selectedWriters.forEach(key => {
                        if (key.startsWith('writer-')) {
                          const idx = parseInt(key.replace('writer-', ''));
                          if (proData.writers[idx]) {
                            writersToImport.push(proData.writers[idx]);
                          }
                        } else if (key.startsWith('unmatched-')) {
                          const idx = parseInt(key.replace('unmatched-', ''));
                          if (proData.unmatchedWriters[idx]) {
                            writersToImport.push({...proData.unmatchedWriters[idx], match_confidence: 'manual'});
                          }
                        }
                      });
                      
                      // Get selected publishers (both main and sub)
                      const publishersToImport = [];
                      selectedPublishers.forEach(key => {
                        if (key.startsWith('pub-')) {
                          const idx = parseInt(key.replace('pub-', ''));
                          const pubs = proData.mainPublishers || proData.publishers || [];
                          if (pubs[idx]) {
                            publishersToImport.push({...pubs[idx], publisher_type: 'main'});
                          }
                        } else if (key.startsWith('subpub-')) {
                          const idx = parseInt(key.replace('subpub-', ''));
                          if (proData.subPublishers?.[idx]) {
                            publishersToImport.push({...proData.subPublishers[idx], publisher_type: 'sub'});
                          }
                        }
                      });
                      
                      // Update proData with only selected items for the SplitEditor
                      setProData(prev => ({
                        ...prev,
                        selectedWriters: writersToImport,
                        selectedPublishers: publishersToImport
                      }));
                      
                      toast.success(`Ready to import ${writersToImport.length} writers and ${publishersToImport.length} publishers. Click "Import Rights Data" in Split Editor.`);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Prepare Import ({selectedWriters.size + selectedPublishers.size})
                  </Button>
                </div>

                {/* Work ID info - hidden for privacy */}
                {proData.bmiWorkId && (
                  <div className="mt-4 p-3 bg-syncchain-surface/20 rounded-lg">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-syncchain-text-muted">Work ID: <span className="font-mono">{proData.bmiWorkId}</span></span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <SplitEditor 
            splits={publishing.splits || []} 
            onSave={handleSplitsUpdate}
            trackTitle={recording.title}
            trackArtist={recording.artist}
            proData={proData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SearchDetail;
